import React, { useState, useEffect } from 'react';
import {
  Info, X, Check, ArrowRight, ClockCounterClockwise, ArrowClockwise, SealCheck,
} from '@phosphor-icons/react';
import {
  getBranchNames,
  getApprovedGroupCodes,
  getTickets1,
  getApprovedDetails,
  removeFirstMemo,
  saveData,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function MemoReapprovalForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchList, setBranchList] = useState([]);
  const [approvedGroupCodes, setApprovedGroupCodes] = useState([]);
  const [tickets, setTickets] = useState([]);

  const [cao, setCao] = useState('');
  const [branchschema, setBranchschema] = useState('');
  const [branchCode, setBranchCode] = useState('');

  const [group, setGroup] = useState('');
  const [groupCode, setGroupCode] = useState('');

  const [ticket, setTicket] = useState('');
  const [, setSelectedTicketNo] = useState(null);

  const [approvedDate, setApprovedDate] = useState('');
  const [approvedFileName, setApprovedFileName] = useState('');

  const [reason, setReason] = useState('');

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingGroupCode, setPendingGroupCode] = useState(null);
  const [pendingTicketNo, setPendingTicketNo] = useState(null);
  const [pendingAudit, setPendingAudit] = useState(null);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getBranchNames().then((res) => setBranchList(res || [])).catch(() => setBranchList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const resetFromTicket = () => {
    setSelectedTicketNo(null);
    setApprovedDate('');
    setApprovedFileName('');
  };

  const resetFromGroup = () => {
    setGroupCode('');
    setTickets([]);
    setTicket('');
    resetFromTicket();
  };

  const resetFromBranch = () => {
    setBranchschema('');
    setBranchCode('');
    setApprovedGroupCodes([]);
    setGroup('');
    resetFromGroup();
  };

  const handleBranchChange = async (branchName) => {
    setCao(branchName);
    resetFromBranch();
    if (!branchName) return;

    const selected = branchList.find((b) => b.branch_name === branchName);
    if (!selected) return;

    const schema = selected.branch_code;
    setBranchschema(schema);
    setBranchCode(schema);

    try {
      const res = await getApprovedGroupCodes(schema, schema);
      setApprovedGroupCodes(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setApprovedGroupCodes([]);
    }
  };

  const handleGroupChange = async (groupcode) => {
    setGroup(groupcode);
    resetFromGroup();
    if (!groupcode) return;

    setGroupCode(groupcode);

    try {
      const res = await getTickets1(groupcode, branchCode, branchschema);
      setTickets(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTickets([]);
    }
  };

  const handleTicketChange = async (ticketno) => {
    setTicket(ticketno);
    resetFromTicket();
    if (!ticketno) return;

    setSelectedTicketNo(ticketno);

    try {
      const res = await getApprovedDetails(groupCode, ticketno);
      if (res && res.length > 0) {
        const detail = res[0];
        setApprovedDate(detail.approved_date ? new Date(detail.approved_date).toLocaleDateString('en-IN') : '');
        setApprovedFileName(detail.approved_file_name || '');
      } else {
        showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      }
    } catch {
      setApprovedDate('');
      setApprovedFileName('');
    }
  };

  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);
  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const ticketInvalid = touched && !ticket;

  const isValid = !!cao && !!group && !!ticket
    && reason.trim().length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (!group || !ticket) {
      showToast('warn', 'Incomplete Form', 'Please select a valid Group Code and Ticket No.');
      return;
    }

    const trimmedReason = reason.trim();

    setPendingGroupCode(group);
    setPendingTicketNo(Number(ticket));
    setPendingReason(trimmedReason);

    setPendingChanges([
      {
        label: 'First Memo Status',
        oldValue: approvedFileName ? `Approved (${approvedFileName})` : 'Approved',
        newValue: 'Cleared / Pending Re-Approval',
      },
    ]);

    setPendingAudit({
      schemaName: branchschema,
      loginName,
      loginTime: new Date().toISOString(),
      changeType: 'FIRST_MEMO_REAPPROVAL',
      oldData: { approvedDate, approvedFileName },
      newData: { firstMemoStatus: null },
      reason: trimmedReason,
    });

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
    setPendingGroupCode(null);
    setPendingTicketNo(null);
    setPendingAudit(null);
    setPendingChanges([]);
  };

  const confirmSave = async () => {
    if (!pendingGroupCode || !pendingTicketNo || !pendingAudit) return;

    setSaving(true);
    try {
      const res = await removeFirstMemo(pendingGroupCode, pendingTicketNo);

      if (res?.success) {
        try {
          await saveData([pendingAudit]);
          setSaving(false);
          setShowConfirmModal(false);
          showToast('success', 'Success', 'ReApproval processed and audit saved successfully.', true);
          handleClear();
        } catch (auditErr) {
          setSaving(false);
          setShowConfirmModal(false);
          console.error('Audit save failed:', auditErr);
          showToast('warn', 'Partial Success', 'ReApproval done, but audit log failed to save.', true);
          handleClear();
        }
      } else {
        setSaving(false);
        setShowConfirmModal(false);
        showToast('error', 'ReApproval Failed', res?.message || 'Unknown error', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('RemoveFirstMemo failed:', err);
      showToast('error', 'ReApproval Failed', 'Could not process the re-approval. Please try again.', true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setReason('');
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="memo-reapproval-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="memo-reapproval-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SealCheck size={18} color="#002FA7" />
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>First Memo Approval</div>
            </div>
          </div>
          <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', padding: '4px 10px', borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <ClockCounterClockwise size={12} /> In Progress
          </span>
        </div>

        {/* SECTION A: Select Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Select Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose branch, group code, and ticket to load approval details</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchList}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleBranchChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="memo-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Branch is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={approvedGroupCodes}
              getLabel={(o) => o.groupcode}
              getValue={(o) => o.groupcode}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!branchschema}
              error={groupInvalid}
              testId="memo-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`ticket-${formKey}`}
              options={tickets}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={ticket}
              onChange={handleTicketChange}
              placeholder="Select ticket no"
              emptyMessage="Select a group code first"
              disabled={!groupCode}
              error={ticketInvalid}
              testId="memo-ticket-select"
            />
            {ticketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!cao || !group || !ticket} hint="Select branch, group code and ticket in section A first">
        {/* SECTION B: Approved Details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Approved Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Read-only details loaded from the first memo approval</div>
          </div>
        </div>

        <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', padding: '14px', background: '#F9FAFB' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '10px' }}>
            <ClockCounterClockwise size={13} /> Approved Details
            <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Approved Date</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={approvedDate} readOnly data-testid="memo-approved-date" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>Approved File Name</label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={approvedFileName} readOnly data-testid="memo-approved-filename" />
            </div>
          </div>
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!cao || !group || !ticket} hint="Select branch, group code and ticket in section A first">
        {/* SECTION C: Remarks */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Remarks</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Remarks <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="memo-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : reason.trim().length < 10 ? 'Minimum 10 characters required' : 'Maximum 200 characters allowed'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="memo-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="memo-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'ReApproval'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="memo-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <SealCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm First Memo Re-Approval</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to clear the first memo approval for this ticket. Please review before confirming.
              </p>

              {pendingChanges.map((change) => (
                <div key={change.label} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (Old)</p>
                    <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{change.oldValue}</p>
                  </div>
                  <ArrowRight size={14} color="#9CA3AF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>{change.label} (New)</p>
                    <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{change.newValue}</p>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="memo-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="memo-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MemoReapprovalForm;